import axios from 'axios';
import { getToken } from './auth';

import { API_URL } from './config';

const withAuth = () => ({
  headers: { Authorization: `Bearer ${getToken()}` },
  withCredentials: true,
});

export const getBarcodePricingRule = async () =>
  (await axios.get(`${API_URL}/settings/barcode-pricing-rule`, withAuth())).data;

export const updateBarcodePricingRule = async (rule) =>
  (await axios.put(`${API_URL}/settings/barcode-pricing-rule`, rule || {}, withAuth())).data;

export const getBranchRequestConfig = async () =>
  (await axios.get(`${API_URL}/settings/branch-request-config`, withAuth())).data;

export const updateBranchRequestConfig = async (config) =>
  (await axios.put(`${API_URL}/settings/branch-request-config`, config || {}, withAuth())).data;

export const getPosSoldOutDisplaySettings = async () =>
  (await axios.get(`${API_URL}/settings/pos-sold-out-display`, withAuth())).data;

export const updatePosSoldOutDisplaySettings = async (settings) =>
  (await axios.put(`${API_URL}/settings/pos-sold-out-display`, settings || {}, withAuth())).data;

export const getPrintReceiptRedirectDefaults = async () =>
  (await axios.get(`${API_URL}/settings/print-receipt-redirect-defaults`, withAuth())).data;

export const updatePrintReceiptRedirectDefaults = async (settings) =>
  (await axios.put(`${API_URL}/settings/print-receipt-redirect-defaults`, settings || {}, withAuth())).data;

export const getCategoryBarcodes = async ({ includeInactive = false } = {}) => {
  const params = new URLSearchParams();
  if (includeInactive) params.set('includeInactive', 'true');
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return (await axios.get(`${API_URL}/settings/category-barcodes${suffix}`, withAuth())).data;
};

export const regenerateCategoryBarcodes = async () =>
  (await axios.post(`${API_URL}/settings/category-barcodes/regenerate`, {}, withAuth())).data;

export const getPurchaseOrderReasonsSettings = async () =>
  (await axios.get(`${API_URL}/settings/purchase-order-reasons`, withAuth())).data;

export const updatePurchaseOrderReasonsSettings = async (reasons) =>
  (await axios.put(`${API_URL}/settings/purchase-order-reasons`, reasons || {}, withAuth())).data;

export const getPurchaseOrderNotificationSettings = async () =>
  (await axios.get(`${API_URL}/settings/purchase-order-notifications`, withAuth())).data;

export const updatePurchaseOrderNotificationSettings = async (settings) =>
  (await axios.put(`${API_URL}/settings/purchase-order-notifications`, settings || {}, withAuth())).data;

export const getClientPricingCategories = async (params = {}) =>
  (await axios.get(`${API_URL}/settings/client-pricing-categories`, { ...withAuth(), params })).data;

export const createClientPricingCategory = async (data) =>
  (await axios.post(`${API_URL}/settings/client-pricing-categories`, data || {}, withAuth())).data;

export const updateClientPricingCategory = async (id, data) =>
  (await axios.patch(`${API_URL}/settings/client-pricing-categories/${id}`, data || {}, withAuth())).data;

export const getClientPricingDiscountCatalog = async (id, params = {}) =>
  (await axios.get(`${API_URL}/settings/client-pricing-categories/${id}/discounts`, { ...withAuth(), params })).data;

export const updateClientPricingDiscount = async (categoryId, varietyPriceId, data) =>
  (await axios.put(
    `${API_URL}/settings/client-pricing-categories/${categoryId}/discounts/${varietyPriceId}`,
    data || {},
    withAuth()
  )).data;

export const bulkUpdateClientPricingDiscounts = async (categoryId, data) =>
  (await axios.post(
    `${API_URL}/settings/client-pricing-categories/${categoryId}/discounts/bulk`,
    data || {},
    withAuth()
  )).data;

export const getPriceRecommendationSettings = async (branchId) =>
  (await axios.get(`${API_URL}/branch/${branchId}/price-recommendations/settings`, withAuth())).data;

export const updatePriceRecommendationSettings = async (branchId, settings) =>
  (await axios.put(`${API_URL}/branch/${branchId}/price-recommendations/settings`, settings || {}, withAuth())).data;

export const getDteSettings = async () =>
  (await axios.get(`${API_URL}/settings/dte`, withAuth())).data;

export const updateDteCompanyProfile = async (profile) =>
  (await axios.put(`${API_URL}/settings/dte/company-profile`, profile || {}, withAuth())).data;

export const updateDteBranchProfile = async (branchId, profile) =>
  (await axios.put(`${API_URL}/settings/dte/branches/${branchId}`, profile || {}, withAuth())).data;

export const storeDteApiKey = async (apiKey, environment) =>
  (await axios.post(`${API_URL}/settings/dte/api-key`, { apiKey, environment }, withAuth())).data;

export const uploadDteCertificate = async ({ file, certificateRut, password, environment }) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('certificateRut', certificateRut || '');
  formData.append('password', password || '');
  if (environment) formData.append('environment', environment);
  return (await axios.post(`${API_URL}/settings/dte/certificate`, formData, {
    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'multipart/form-data',
    },
    withCredentials: true,
  })).data;
};

export const uploadDteCaf = async ({ file, environment }) => {
  const formData = new FormData();
  formData.append('file', file);
  if (environment) formData.append('environment', environment);
  return (await axios.post(`${API_URL}/settings/dte/cafs/upload`, formData, {
    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'multipart/form-data',
    },
    withCredentials: true,
  })).data;
};

export const fetchDteCaf = async ({ dteType, quantity, environment }) =>
  (await axios.post(`${API_URL}/settings/dte/cafs/fetch`, { dteType, quantity, environment }, withAuth())).data;

export const updateDteCafStatus = async (cafId, { status, reason }) =>
  (await axios.patch(`${API_URL}/settings/dte/cafs/${cafId}/status`, { status, reason }, withAuth())).data;

export const resetDteCafNextFolio = async (cafId, nextFolio) =>
  (await axios.post(`${API_URL}/settings/dte/cafs/${cafId}/reset-next-folio`, { nextFolio }, withAuth())).data;

export const getDteCafAudits = async (cafId) =>
  (await axios.get(`${API_URL}/settings/dte/cafs/${cafId}/audit`, withAuth())).data;

export const getDteCafAdminSummary = async () =>
  (await axios.get(`${API_URL}/settings/dte/cafs/admin`, withAuth())).data;

export const lookupDteTaxpayer = async ({ rut, environment }) =>
  (await axios.post(`${API_URL}/settings/dte/taxpayer-lookup`, { rut, environment }, withAuth())).data;

export const updateDteAutomation = async (automation, environment) =>
  (await axios.put(`${API_URL}/settings/dte/automation`, { automation, environment }, withAuth())).data;
